import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import Layout from './Layout';
import './IntelligenceDashboard.css';
import { dashboardService } from '../services/dashboardService';
import HamsterFloatingButton from './HamsterFloatingButton';
import { useUserInfo } from '../contexts/UserContext';
import HeroSection from './dashboard/sections/HeroSection';
import ModuleCard from './dashboard/cards/ModuleCard';
import { bunkersMoneyTiles } from './dashboard/cards/bunkersMoneyTiles';
import Footer from './ui/Footer';
import OfflineDashboardStrip from './offline/OfflineDashboardStrip';

const IntelligenceDashboard = ({ initialSection }) => {
    const history = useHistory();
    const { userInfo, updateUserInfo } = useUserInfo();
    const [metrics, setMetrics] = useState({
        deals: 0,
        trade_recaps: 0,
        deliveries: 0,
        deals_solids: 0,
        deals_fuels: 0,
        trade_recaps_solids: 0,
        trade_recaps_fuels: 0,
        deliveries_solids: 0,
        deliveries_fuels: 0,
        fuel_enquiries_open_fuels: 0,
        fuel_enquiries_credit_pending: 0,
        fuel_enquiries_awaiting_pricing: 0,
        fuel_enquiries_final_review: 0,
        crm_active_profiles: 0,
        crm_approved_kyc: 0,
        crm_pending_review: 0,
        crm_flagged: 0,
        stock_items: 0,
        stock_variants: 0,
        stock_contracts: 0,
        total_invoices: 0,
        outgoing_invoices: 0,
        proforma_invoices: 0,
        incoming_invoices: 0,
        purchase_orders: 0,
        legal_total_contracts: 0,
        legal_active_contracts: 0,
        legal_drafts: 0
    });
    const [metricsLoading, setMetricsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                updateUserInfo(userInfoData);
            } catch (err) {
                console.error('Error fetching user info:', err);
                setError('Failed to load user information');
            }
        };

        fetchUserInfo();
    }, [updateUserInfo]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setMetricsLoading(true);
                const metricsData = await dashboardService.getMetrics();
                setMetrics(metricsData);
                setError(null);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data');
            } finally {
                setMetricsLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // While metrics are still loading, show a dash instead of the zero-valued
    // initial state so cards don't flash misleading "0" counts on first paint.
    const displayMetrics = (items) => (
        metricsLoading ? items.map((item) => ({ ...item, value: '—' })) : items
    );

    if (error) {
        return (
            <Layout>
                <div className="intelligence-dashboard error">
                    <div className="error-message">{error}</div>
                </div>
            </Layout>
        );
    }

    // --- Per-module access ---
    // Every card is always rendered; cards the user has no access to are shown
    // in a locked state instead of being hidden, so a newly-provisioned user
    // sees the full module set plus guidance on how to request access.
    const isPrivileged = !!(userInfo?.is_superuser || userInfo?.is_staff);
    // Until the access flags have loaded, treat everything as accessible so
    // privileged users don't see a flash of locked cards on first paint.
    const accessResolved = !!userInfo && Object.prototype.hasOwnProperty.call(userInfo, 'has_full_access');
    const hasFull = !accessResolved || !!userInfo?.has_full_access || isPrivileged;
    const canTradeOps = hasFull || !!userInfo?.has_cargo_access;
    const canBunkers = hasFull || !!userInfo?.has_bunkers_access;
    const canProduct = hasFull;
    const canLegal = hasFull;
    const canCrm = hasFull || !!userInfo?.has_crm_access || !!userInfo?.has_financial_access;
    const canFinancials = hasFull || !!userInfo?.has_financial_access || !!userInfo?.is_financial_approver;

    const moduleAccess = [canTradeOps, canBunkers, canProduct, canCrm, canFinancials, canLegal];
    const lockedCount = moduleAccess.filter((granted) => !granted).length;
    const allLocked = accessResolved && lockedCount === moduleAccess.length;
    const showAccessNotice = accessResolved && lockedCount > 0;

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="intelligence-dashboard-new">
                {/* Hero Section */}
                <HeroSection metrics={metrics} userInfo={userInfo} />

                {/* Module Cards Section */}
                <section className="modules-section">
                    {/* Offline access — a mode, not a module, so it sits above
                        the grid rather than becoming a seventh card. */}
                    <OfflineDashboardStrip />

                    {/* Access notice — shown when one or more modules are locked */}
                    {showAccessNotice && (
                        <div className="module-access-notice" role="status">
                            <div className="module-access-notice-icon">
                                <FontAwesomeIcon icon={faLock} />
                            </div>
                            <div className="module-access-notice-text">
                                <h3>
                                    {allLocked
                                        ? "Your account doesn't have module access yet"
                                        : 'Some modules are locked'}
                                </h3>
                                <p>
                                    Module access is granted by an IMT administrator. Please contact your
                                    system administrator to request access to the modules you need.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="modules-grid">
                        {/* Trade Ops Solids Card - Requires Full Access */}
                        <ModuleCard
                            title="Trade Ops Cargo"
                            backgroundClass="trade-operations-bg"
                            moduleType="trade-operations-solids"
                            route="/dashboard/trades/solids"
                            locked={!canTradeOps}
                            metrics={displayMetrics([
                                { value: metrics.deals_solids || 0, label: 'Active Deals' },
                                { value: metrics.trade_recaps_solids || 0, label: 'Trade Recaps' },
                                { value: metrics.deliveries_solids || 0, label: 'Deliveries' }
                            ])}
                            delay={0}
                        />

                        {/* Trade Ops Fuels Card - Requires Full Access */}
                        <ModuleCard
                            title="Trade Ops Bunkers"
                            backgroundClass="trade-operations-fuels-bg"
                            moduleType="trade-operations-fuels"
                            route="/dashboard/trades/fuels"
                            locked={!canBunkers}
                            metrics={displayMetrics([
                                    { value: metrics.trade_recaps_fuels || 0, label: 'Trade Recaps' },
                                    { value: metrics.deliveries_fuels || 0, label: 'Deliveries' },
                                    { value: metrics.fuel_enquiries_open_fuels || 0, label: 'Open Enquiries' },
                                    { value: metrics.fuel_enquiries_credit_pending || 0, label: 'Credit Pending' },
                                    { value: metrics.fuel_enquiries_awaiting_pricing || 0, label: 'Awaiting Pricing' },
                                    { value: metrics.fuel_enquiries_final_review || 0, label: 'Final Review' },
                                    // Only when the server sent them (ledger row 149).
                                    ...bunkersMoneyTiles(metrics)
                            ])}
                            delay={100}
                        />

                        {/* Product Module Card - Requires Full Access */}
                        <ModuleCard
                            title="Product Module"
                            backgroundClass="product-module-bg"
                            moduleType="product-module"
                            route="/dashboard/risk-analytics"
                            locked={!canProduct}
                            metrics={displayMetrics([
                                { value: metrics.stock_items || 0, label: 'Product Items' },
                                { value: metrics.stock_variants || 0, label: 'Variants' },
                                { value: metrics.stock_contracts || 0, label: 'Contracts' }
                            ])}
                            delay={200}
                        />

                        {/* CRM Card - Requires CRM Access */}
                        <ModuleCard
                            title="CRM"
                            backgroundClass="crm-bg"
                            moduleType="crm"
                            route="/dashboard/crm"
                            locked={!canCrm}
                            metrics={displayMetrics([
                                { value: metrics.crm_active_profiles || 0, label: 'Active Profiles' },
                                { value: metrics.crm_approved_kyc || 0, label: 'Approved KYC' },
                                { value: metrics.crm_pending_review || 0, label: 'Pending Review' }
                            ])}
                            delay={400}
                        />

                        {/* Financials Card - Requires Financial Access */}
                        <ModuleCard
                            title="Financials"
                            backgroundClass="financials-bg"
                            moduleType="financials"
                            route="/invoicing/dashboard"
                            locked={!canFinancials}
                            metrics={displayMetrics([
                                { value: metrics.outgoing_invoices || 0, label: 'Outgoing Invoices' },
                                { value: metrics.proforma_invoices || 0, label: 'Pro-forma' },
                                { value: metrics.incoming_invoices || 0, label: 'Incoming Invoices' },
                                { value: metrics.purchase_orders || 0, label: 'Purchase Orders' }
                            ])}
                            delay={600}
                        />

                        {/* Legal Card — Bunkers contracts overview.
                            Locked unless the user has full access, mirroring the
                            ProtectedRoute on /dashboard/legal (requiresFullAccess). */}
                        <ModuleCard
                            title="Legal"
                            backgroundClass="legal-bg"
                            moduleType="legal"
                            route="/dashboard/legal"
                            locked={!canLegal}
                            metrics={displayMetrics([
                                { value: metrics.legal_total_contracts || 0, label: 'Total Contracts' },
                                { value: metrics.legal_active_contracts || 0, label: 'Active' },
                                { value: metrics.legal_drafts || 0, label: 'Drafts' }
                            ])}
                            delay={800}
                        />

                        {/* Schernikau Group Card - IMT-only corporate structure module */}
                        {(userInfo?.has_full_access || userInfo?.is_superuser || userInfo?.is_staff) && (
                            <ModuleCard
                                title="Schernikau Group"
                                backgroundClass="corporate-structure-bg"
                                moduleType="corporate-structure"
                                route="/dashboard/corporate-structure"
                                metrics={displayMetrics([
                                    { value: metrics.corp_entities || 0, label: 'Entities' },
                                    { value: metrics.corp_consolidation || 0, label: 'Consolidation' },
                                    { value: metrics.corp_countries || 0, label: 'Countries' }
                                ])}
                                delay={700}
                            />
                        )}
                    </div>
                </section>

                {/* Hamster Chat Integration */}
                <HamsterFloatingButton />

                {/* Footer */}
                <Footer />
            </div>
        </Layout>
    );
};

export default IntelligenceDashboard;
